import ComposingState from 'components/Keyboard/cache/composing';
import { CandidatesKeyboard, KeyboardModule } from 'components/Keyboard/types';
import { SPECIAL_KEYS } from 'components/Keyboard/utils/constants';

import candidatesModule, { CandidateProvider } from '../../../modules/candidates';
import { formatPinyinDisplay, getPinyinCandidates } from '../Pinyin/provider';
import { CHINESE_KEYBOARD_NAME } from '../constants';
import { formatZhuyinDisplay, getZhuyinCandidates } from '../Zhuyin/provider';
import { getCangjieCandidates, shouldCommitOnOverflow } from '../Cangjie/provider';
import { isCangjieRadical } from '../Cangjie/utils';

type ChineseProvider = {
  getCandidates: CandidateProvider;
  format?: (input: string) => string;
  isComposingKey?: (button: string) => boolean;
};

const PROVIDERS: Record<string, ChineseProvider> = {
  [CHINESE_KEYBOARD_NAME.PINYIN]: {
    getCandidates: getPinyinCandidates,
    format: formatPinyinDisplay,
  },
  [CHINESE_KEYBOARD_NAME.ZHUYIN]: {
    getCandidates: getZhuyinCandidates,
    format: formatZhuyinDisplay,
  },
  [CHINESE_KEYBOARD_NAME.CANGJIE]: {
    getCandidates: getCangjieCandidates,
    isComposingKey: isCangjieRadical,
  },
};

export function chineseModule(keyboard: CandidatesKeyboard) {
  keyboard.registerModule('chinese', (module: KeyboardModule) => {
    module.fn = {
      handleButtonClicked: keyboard.handleButtonClicked,
    };

    keyboard.handleButtonClicked = async (...args) => {
      const [button] = args;
      const keyboardName = keyboard.getOptions()?.keyboardName;

      if (button === SPECIAL_KEYS.NEXT_KEYBOARD) {
        keyboard.flushCandidates?.();
      }

      if (button === SPECIAL_KEYS.SPACE) {
        const confirmed = keyboard.confirmFirstCandidate?.();
        if (confirmed) {
          return;
        }
      }

      if (button === SPECIAL_KEYS.ENTER) {
        if (ComposingState.get()) {
          if (keyboard.confirmFirstCandidate?.()) {
            return;
          }
          keyboard.flushCandidates?.();
          keyboard.setCaretPosition(keyboard.getInput().length);
        }
        if (keyboard.getOptions()?.type !== 'textarea') {
          return;
        }
      }

      if (keyboardName === CHINESE_KEYBOARD_NAME.CANGJIE && isCangjieRadical(button)) {
        const overflow = await shouldCommitOnOverflow(
          ComposingState.get(),
          button,
          (glyphs) => getCangjieCandidates(glyphs, undefined, keyboard),
        );
        if (overflow) {
          keyboard.confirmFirstCandidate?.();
        }
      }

      return module.fn.handleButtonClicked.call(keyboard, ...args);
    };
  });
}

export function chineseCandidatesModule(keyboard: CandidatesKeyboard) {
  // Resolve the provider on every call so it follows the current keyboardName:
  // 🌐 (NEXT_KEYBOARD) switches the layout via multiKeyboards, and candidates/format
  // must switch with it instead of staying bound to the keyboard active at registration.
  const resolveProvider = (): ChineseProvider => {
    const keyboardName: string = keyboard.getOptions()?.keyboardName ?? CHINESE_KEYBOARD_NAME.ZHUYIN;
    return PROVIDERS[keyboardName];
  };

  const getCandidates: CandidateProvider = (input, maxResults, targetKeyboard) =>
    resolveProvider().getCandidates(input, maxResults, targetKeyboard);

  // Default to identity so layouts without a formatter need not define one.
  const format = (input: string): string => (resolveProvider().format ?? ((value: string) => value))(input);

  const isComposingKey = (button: string): boolean =>
    (resolveProvider().isComposingKey ?? (() => true))(button);

  return candidatesModule(getCandidates, format, isComposingKey)(keyboard);
}